import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  LuArrowLeft,
  LuClipboardList,
  LuPenSquare,
  LuShoppingCart,
} from "react-icons/lu";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import DetailAccountInfo from "../../components/materialLines/DetailAccountInfo";
import DetailHeader from "../../components/materialLines/DetailHeader";
import DetailPartyInfo from "../../components/materialLines/DetailPartyInfo";
import DetailSection from "../../components/materialLines/DetailSection";
import MaterialPhoto from "../../components/materialLines/MaterialPhoto";
import OrderStatusBadge from "../../components/materialLines/OrderStatusBadge";
import MaterialPurchaseList from "../../components/materialPurchases/MaterialPurchaseList";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { LoadingState } from "../../components/ui/TableStateRow";
import cx from "../../components/ui/cx";
import {
  detailCardClass,
  detailIndexHeadClass,
  detailInfoLabelClass,
  detailNumberCellClass,
  detailNumberHeadClass,
  detailNumberTotalClass,
  detailPhotoHeadClass,
  detailTableHeadRowClass,
  detailTableWrapClass,
  mobileCardClass,
  moneyCellClass,
  pageTitleClass,
  tableHeadCellClass,
} from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import {
  clearCurrentMaterialOrder,
  fetchMaterialOrderDetail,
  getCurrentMaterialOrder,
  getCurrentMaterialOrderLoading,
  refreshMaterialOrderDetail,
} from "../../features/materialOrders/materialOrdersSlice";
import { updatePdfFile } from "../../features/settings/settingsSlice";
import { makeReport } from "../../services/download.service";
import { deleteMaterialPurchase } from "../../services/materialPurchase.service";
import {
  formatGujaratiLongDateTime,
  formatQty,
} from "../../static/materialLine";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { BreadcrumbType } from "../../types/common";
import { MaterialOrderItemProps } from "../../types/materialOrder";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "મટીરીયલ ઓર્ડર", href: route.materialOrders },
  { label: "મટીરીયલ ઓર્ડર વિગતો" },
];

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const closedBadgeClass = "bg-green-100 text-green-800 hover:bg-green-100";

const totalCellClass = "font-bold text-[#113F67]";

const sum = (items: MaterialOrderItemProps[], pick: (item: MaterialOrderItemProps) => number) =>
  items.reduce((total, item) => total + (Number(pick(item)) || 0), 0);

const pendingClass = (item: MaterialOrderItemProps) =>
  (item.pending_qty || 0) > 0 ? "text-orange-600" : "text-gray-400";

const MaterialOrderDetailPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const order = useAppSelector(getCurrentMaterialOrder);
  const isLoading = useAppSelector(getCurrentMaterialOrderLoading);
  const loggingUser = useAppSelector(getLoggingUser);

  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.materialOrdersUpdate
  );
  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.materialOrdersReport
  );

  const [isReportLoading, setIsReportLoading] = useState<boolean>(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchMaterialOrderDetail(Number(id)));
    }
    return () => {
      dispatch(clearCurrentMaterialOrder());
    };
  }, [id, dispatch]);

  const items = order?.items || [];
  const purchases = order?.purchases || [];

  // Back to where the user came from, or to the order list when the page was opened directly.
  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(route.materialOrders);
    }
  };

  const handleReportExport = async () => {
    setIsReportLoading(true);
    const file = await makeReport(`/v1/admin/material-orders/${id}/pdf`);
    setIsReportLoading(false);

    if (file) dispatch(updatePdfFile(file));
  };

  // Deleting a purchase changes what the order received and maybe its status.
  const handleDeletePurchase = async (purchaseId: number) => {
    if (await deleteMaterialPurchase(purchaseId)) {
      await dispatch(refreshMaterialOrderDetail(Number(id)));
    }
  };

  const photo = (item: MaterialOrderItemProps) => (
    <MaterialPhoto
      name={item.material?.name || "-"}
      imageUrl={item.material?.image_url}
      className="h-10 w-10 shrink-0 rounded-md"
      compact
    />
  );

  // A purchase closed the line before all of it came, so nothing is pending on it.
  const closedBadge = (item: MaterialOrderItemProps) =>
    item.closed && (
      <Badge variant="none" className={closedBadgeClass}>
        બંધ
      </Badge>
    );

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>મટીરીયલ ઓર્ડર વિગતો</h1>
      </div>

      {isLoading ? (
        <div className={detailCardClass}>
          <LoadingState />
        </div>
      ) : !order ? (
        <div className={cx(detailCardClass, "text-center text-gray-500")}>
          મટીરીયલ ઓર્ડર ની વિગતો મળી નથી
        </div>
      ) : (
        <div className={detailCardClass}>
          <DetailHeader icon={LuClipboardList} title="મટીરીયલ ઓર્ડર" id={order.id}>
            <Button variant="outline" className="max-sm:flex-1" onClick={handleBack}>
              <LuArrowLeft className="h-4 w-4" />
              પાછા જાઓ
            </Button>

            {canReport && (
              <Button
                variant="primary"
                className="max-sm:flex-1"
                disabled={isReportLoading}
                onClick={handleReportExport}
              >
                {isReportLoading ? "રિપોર્ટ બની રહ્યો છે..." : "રિપોર્ટ બનાવો"}
              </Button>
            )}

            {canUpdate && (
              <Button
                variant="primary"
                className="max-sm:flex-1"
                onClick={() =>
                  navigate(
                    route.materialOrderEdit.replace(
                      ":id",
                      order.id as unknown as string
                    )
                  )
                }
              >
                <LuPenSquare className="h-4 w-4" />
                એડિટ કરો
              </Button>
            )}
          </DetailHeader>

          <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-2">
            <div className="flex flex-wrap items-center gap-x-2">
              <span className={detailInfoLabelClass}>તારીખ:</span>
              <span>
                {formatGujaratiLongDateTime(order.date)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <span className={detailInfoLabelClass}>સ્થિતિ:</span>
              <OrderStatusBadge status={order.status} />
            </div>
          </div>

          <DetailPartyInfo partyId={order.party_id} party={order.party} />

          <DetailSection title="મટીરીયલ ઓર્ડર આઇટમ્સ ની માહિતી">
            <div className={cx(detailTableWrapClass, "max-sm:hidden")}>
              <Table>
                <TableHeader>
                  <TableRow className={detailTableHeadRowClass}>
                    <TableHead className={detailIndexHeadClass}>ક્રમ</TableHead>
                    <TableHead className={detailPhotoHeadClass}>ફોટો</TableHead>
                    <TableHead className={tableHeadCellClass}>મટીરીયલ</TableHead>
                    <TableHead className={detailNumberHeadClass}>મળેલ</TableHead>
                    <TableHead className={detailNumberHeadClass}>બાકી</TableHead>
                    <TableHead className={detailNumberHeadClass}>જથ્થો</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell>{photo(item)}</TableCell>
                      <TableCell className="break-words font-semibold text-[#113F67]">
                        {item.material?.name || "-"}
                      </TableCell>
                      <TableCell className={detailNumberCellClass}>
                        {formatQty(item.received_qty)}
                      </TableCell>
                      {/* The બંધ badge goes before the qty, so the qty stays in line with the others. */}
                      <TableCell className={detailNumberCellClass}>
                        <span className="inline-flex items-center justify-end gap-2">
                          {closedBadge(item)}
                          {formatQty(item.pending_qty)}
                        </span>
                      </TableCell>
                      <TableCell className={detailNumberCellClass}>
                        {formatQty(item.qty)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                <TableFooter>
                  <TableRow className="bg-[#113F67]/10 hover:bg-[#113F67]/10">
                    <TableCell colSpan={3} className={cx(totalCellClass, "text-right")}>
                      કુલ:
                    </TableCell>
                    <TableCell className={detailNumberTotalClass}>
                      {formatQty(sum(items, (item) => item.received_qty || 0))}
                    </TableCell>
                    <TableCell className={detailNumberTotalClass}>
                      {formatQty(sum(items, (item) => item.pending_qty || 0))}
                    </TableCell>
                    <TableCell className={detailNumberTotalClass}>
                      {formatQty(sum(items, (item) => item.qty))}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>

            <div className="space-y-4 sm:hidden">
              {items.map((item, index) => (
                <div key={item.id} className={mobileCardClass}>
                  <div className="flex items-center gap-3 border-b p-4">
                    {photo(item)}
                    <span className="break-words font-semibold text-[#113F67]">
                      {item.material?.name || "-"}
                    </span>
                  </div>

                  <div className="space-y-3 p-4 text-sm">
                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>ક્રમ</span>
                      <span className="font-medium">{index + 1}</span>
                    </div>

                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>મળેલ / બાકી</span>
                      <span className="inline-flex items-center justify-end gap-2 text-right font-semibold">
                        {closedBadge(item)}
                        <span>
                          <span className="text-green-700">
                            {formatQty(item.received_qty)}
                          </span>
                          {" / "}
                          <span className={pendingClass(item)}>
                            {formatQty(item.pending_qty)}
                          </span>
                        </span>
                      </span>
                    </div>

                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>જથ્થો</span>
                      <span className={moneyCellClass}>{formatQty(item.qty)}</span>
                    </div>
                  </div>
                </div>
              ))}

              <div className={cx(mobileCardClass, "space-y-3 p-4 text-sm")}>
                <div className={cardRowClass}>
                  <span className={totalCellClass}>કુલ જથ્થો</span>
                  <span className={cx(totalCellClass, "text-right")}>
                    {formatQty(sum(items, (item) => item.qty))}
                  </span>
                </div>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="ખરીદી ની માહિતી" icon={LuShoppingCart}>
            <MaterialPurchaseList
              embedded
              purchases={purchases}
              pageNumber={1}
              pageSize={purchases.length || 1}
              isLoading={false}
              emptyLabel="આ ઓર્ડર ની હજુ કોઈ ખરીદી નથી"
              handleDeletePurchase={handleDeletePurchase}
            />
          </DetailSection>

          <DetailAccountInfo
            creator={order.creator}
            updater={order.updater}
            createdAt={order.created_at}
            updatedAt={order.updated_at}
          />
        </div>
      )}
    </AppLayout>
  );
};
export default MaterialOrderDetailPage;
